import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { StructureRef } from '../../utils/proposalStructureApi';
import type { StructureFilterValue } from '../../utils/ganttSubtaskRows';
import './GanttStructureFilter.css';

interface GanttStructureFilterProps {
  structures: StructureRef[];
  filter: Set<StructureFilterValue> | null;
  onChange: (filter: Set<StructureFilterValue> | null) => void;
}

function filterTriggerLabel(
  filter: Set<StructureFilterValue> | null,
  structures: StructureRef[],
): string {
  if (!filter || filter.size === 0) return 'All structures';
  const names: string[] = [];
  if (filter.has('__unassigned')) names.push('Unassigned');
  for (const s of structures) {
    if (filter.has(s.id)) names.push(s.name);
  }
  if (names.length === 0) return 'All structures';
  if (names.length === 1) return names[0]!;
  return `${names[0]} +${names.length - 1}`;
}

const GanttStructureFilter: React.FC<GanttStructureFilterProps> = ({
  structures,
  filter,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = filter ?? new Set<StructureFilterValue>();
  const hasSelection = selected.size > 0;

  const toggle = (key: StructureFilterValue) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next.size > 0 ? next : null);
  };

  const clearAll = () => onChange(null);

  if (structures.length === 0) return null;

  return (
    <div className={`pm-gantt-structure-filter ${open ? 'pm-gantt-structure-filter--open' : ''}`.trim()} ref={rootRef}>
      <button
        type="button"
        className={`pm-gantt-structure-filter-trigger ${hasSelection ? 'has-value' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="pm-gantt-structure-filter-trigger-label">
          {filterTriggerLabel(filter, structures)}
        </span>
        <ChevronDown size={12} aria-hidden />
      </button>
      {open && (
        <div className="pm-gantt-structure-filter-menu" role="listbox" aria-multiselectable>
          <div className="pm-gantt-structure-filter-options">
            <label className="pm-gantt-structure-filter-option">
              <input
                type="checkbox"
                checked={!hasSelection}
                onChange={() => clearAll()}
              />
              <span>All structures</span>
            </label>
            <label className="pm-gantt-structure-filter-option">
              <input
                type="checkbox"
                checked={selected.has('__unassigned')}
                onChange={() => toggle('__unassigned')}
              />
              <span>Unassigned</span>
            </label>
            {structures.map((s) => (
              <label key={s.id} className="pm-gantt-structure-filter-option">
                <input
                  type="checkbox"
                  checked={selected.has(s.id)}
                  onChange={() => toggle(s.id)}
                />
                <span>{s.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GanttStructureFilter;
